import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CreditCard,
  TrendingUp,
  AlertTriangle,
  DollarSign,
  User,
  Phone,
  Award,
  Search,
  Ban,
  CheckCircle
} from 'lucide-react';
import {
  getDistributors,
  suspendDistributor,
  activateDistributor
} from '../../api/wholesale/distributors';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const CreditManagementPage = () => {
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchDistributors = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getDistributors();
      const data = response?.data || response || [];
      setDistributors(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching distributors:', err);
      setError('Failed to load distributors');
      setDistributors([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDistributors();
  }, []);

  // Calculate summary stats
  const stats = distributors.reduce(
    (acc, dist) => {
      const creditLimit = parseFloat(dist.credit_limit || 0);
      const creditBalance = parseFloat(dist.current_credit_balance || 0);
      const availableCredit = creditLimit - creditBalance;
      const utilizationPercent = creditLimit > 0 ? (creditBalance / creditLimit) * 100 : 0;

      acc.totalCreditExtended += creditLimit;
      acc.totalCreditUsed += creditBalance;
      acc.totalAvailableCredit += availableCredit;

      if (creditBalance > creditLimit) {
        acc.exceededCount++;
        acc.exceededAmount += creditBalance - creditLimit;
      } else if (utilizationPercent > 80) {
        acc.nearLimitCount++;
      }

      if (creditBalance > 0) {
        acc.overdueAmount += creditBalance; // Simplified - would need payment_status
      }

      return acc;
    },
    {
      totalCreditExtended: 0,
      totalCreditUsed: 0,
      totalAvailableCredit: 0,
      exceededCount: 0,
      exceededAmount: 0,
      nearLimitCount: 0,
      overdueAmount: 0
    }
  );

  const creditUtilization =
    stats.totalCreditExtended > 0 ? (stats.totalCreditUsed / stats.totalCreditExtended) * 100 : 0;

  // Filter distributors
  const filteredDistributors = distributors.filter((dist) => {
    const creditLimit = parseFloat(dist.credit_limit || 0);
    const creditBalance = parseFloat(dist.current_credit_balance || 0);
    const utilizationPercent = creditLimit > 0 ? (creditBalance / creditLimit) * 100 : 0;

    const matchesSearch =
      (dist.business_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (dist.distributor_number || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (dist.phone_number || '').includes(searchQuery);

    if (!matchesSearch) return false;

    switch (filterStatus) {
      case 'exceeded':
        return creditBalance > creditLimit;
      case 'nearLimit':
        return utilizationPercent > 80 && creditBalance <= creditLimit;
      case 'active':
        return dist.customer_status === 'active';
      case 'suspended':
        return dist.customer_status === 'suspended';
      default:
        return true;
    }
  });

  const handleSuspend = async (distributorId, businessName) => {
    if (!window.confirm(`Are you sure you want to suspend ${businessName}?`)) return;

    try {
      await suspendDistributor(distributorId, 'Credit limit management');
      await fetchDistributors();
      alert('Distributor suspended successfully');
    } catch (err) {
      console.error('Error suspending distributor:', err);
      alert('Failed to suspend distributor');
    }
  };

  const handleActivate = async (distributorId, businessName) => {
    if (!window.confirm(`Are you sure you want to activate ${businessName}?`)) return;

    try {
      await activateDistributor(distributorId);
      await fetchDistributors();
      alert('Distributor activated successfully');
    } catch (err) {
      console.error('Error activating distributor:', err);
      alert('Failed to activate distributor');
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Credit Management</h1>
        <p className="text-gray-500 mt-1">
          Monitor and manage distributor credit limits and utilization
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-500">Total Credit Extended</p>
            <CreditCard className="h-5 w-5 text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            ₦{stats.totalCreditExtended.toLocaleString()}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-500">Available Credit</p>
            <TrendingUp className="h-5 w-5 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            ₦{stats.totalAvailableCredit.toLocaleString()}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-500">Credit Utilization</p>
            <DollarSign className="h-5 w-5 text-purple-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{creditUtilization.toFixed(1)}%</p>
          <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full ${creditUtilization > 80 ? 'bg-red-500' : creditUtilization > 60 ? 'bg-yellow-500' : 'bg-green-500'}`}
              style={{ width: `${Math.min(creditUtilization, 100)}%` }}
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-500">Credit Warnings</p>
            <AlertTriangle className="h-5 w-5 text-red-500" />
          </div>
          <div className="space-y-1">
            <p className="text-sm text-gray-700">
              <span className="font-semibold text-red-600">{stats.exceededCount}</span> Exceeded
            </p>
            <p className="text-sm text-gray-700">
              <span className="font-semibold text-yellow-600">{stats.nearLimitCount}</span> Near
              Limit
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, number, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
          >
            <option value="all">All Distributors</option>
            <option value="exceeded">Exceeded Limit</option>
            <option value="nearLimit">Near Limit (&gt;80%)</option>
            <option value="active">Active Only</option>
            <option value="suspended">Suspended Only</option>
          </select>
        </div>
      </div>

      {/* Distributors Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Distributor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Credit Limit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current Balance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Available
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Utilization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredDistributors.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    No distributors found
                  </td>
                </tr>
              ) : (
                filteredDistributors.map((dist) => {
                  const creditLimit = parseFloat(dist.credit_limit || 0);
                  const creditBalance = parseFloat(dist.current_credit_balance || 0);
                  const availableCredit = creditLimit - creditBalance;
                  const utilizationPercent =
                    creditLimit > 0 ? (creditBalance / creditLimit) * 100 : 0;

                  const isExceeded = creditBalance > creditLimit;
                  const isNearLimit = utilizationPercent > 80 && !isExceeded;

                  return (
                    <tr
                      key={dist.id}
                      className={`hover:bg-gray-100 cursor-pointer transition-colors ${isExceeded ? 'bg-red-50' : isNearLimit ? 'bg-yellow-50' : ''}`}
                      onClick={() => (window.location.href = `/wholesale/distributors/${dist.id}`)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {dist.business_name}
                            </div>
                            <div className="text-sm text-gray-500">{dist.distributor_number}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ₦{creditLimit.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        ₦{creditBalance.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span
                          className={
                            availableCredit < 0 ? 'text-red-600 font-semibold' : 'text-green-600'
                          }
                        >
                          ₦{availableCredit.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-2 w-20">
                            <div
                              className={`h-2 rounded-full ${isExceeded ? 'bg-red-500' : isNearLimit ? 'bg-yellow-500' : 'bg-green-500'}`}
                              style={{ width: `${Math.min(utilizationPercent, 100)}%` }}
                            />
                          </div>
                          <span
                            className={`text-sm font-medium ${isExceeded ? 'text-red-600' : isNearLimit ? 'text-yellow-600' : 'text-gray-700'}`}
                          >
                            {utilizationPercent.toFixed(0)}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            dist.customer_status === 'active'
                              ? 'bg-green-100 text-green-700'
                              : dist.customer_status === 'suspended'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {dist.customer_status}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CreditManagementPage;
